import { useEditorStore } from "../store";

const PRESETS = [
  {
    name: "Professional",
    colors: {
      primary: "#1e3a8a",
      secondary: "#0284c7",
      accent: "#f59e0b",
      background: "#ffffff",
      surface: "#f8fafc",
      text: "#0f172a",
    },
    typography: { headingFont: "Inter", bodyFont: "Inter" },
  },
  {
    name: "Modern Dark",
    colors: {
      primary: "#6366f1",
      secondary: "#a855f7",
      accent: "#ec4899",
      background: "#0f172a",
      surface: "#1e293b",
      text: "#f8fafc",
    },
    typography: { headingFont: "Outfit", bodyFont: "Inter" },
  },
  {
    name: "Minimalist",
    colors: {
      primary: "#18181b",
      secondary: "#52525b",
      accent: "#3f3f46",
      background: "#fafafa",
      surface: "#ffffff",
      text: "#18181b",
    },
    typography: { headingFont: "Inter", bodyFont: "Inter" },
  },
  {
    name: "Elegant Law",
    colors: {
      primary: "#0f372b",
      secondary: "#155e75",
      accent: "#d97706",
      background: "#fcfbf9",
      surface: "#f3f0ea",
      text: "#1c1917",
    },
    typography: { headingFont: "Playfair Display", bodyFont: "Inter" },
  },
  {
    name: "Bold Vibrant",
    colors: {
      primary: "#d97706",
      secondary: "#ea580c",
      accent: "#0284c7",
      background: "#ffffff",
      surface: "#fef3c7",
      text: "#18181b",
    },
    typography: { headingFont: "Space Grotesk", bodyFont: "Inter" },
  },
];

export function InspectorTheme() {
  const document = useEditorStore((s) => s.document);
  const updateTheme = useEditorStore((s) => s.updateTheme);
  const theme = document.theme ?? {};
  const colors = theme.colors ?? {};

  const handleColorChange = (key: string, value: string) => {
    updateTheme({ colors: { [key]: value } });
  };

  const applyPreset = (preset: (typeof PRESETS)[0]) => {
    updateTheme({
      colors: preset.colors,
      typography: preset.typography,
    });
  };

  return (
    <div className="zirro-inspector-section zirro-theme-editor">
      <div className="zirro-inspector-header">
        <h4>Theme Customization</h4>
      </div>

      <div className="zirro-field">
        <span>Design Presets</span>
        <div className="zirro-preset-grid">
          {PRESETS.map((p) => (
            <button
              key={p.name}
              type="button"
              className="zirro-preset-btn"
              onClick={() => applyPreset(p)}
              title={p.name}
            >
              <div
                className="zirro-preset-preview"
                style={{
                  background: p.colors.background,
                  borderColor: p.colors.primary,
                }}
              >
                <span style={{ color: p.colors.primary }}>■</span>
                <span style={{ color: p.colors.secondary }}>■</span>
                <span style={{ color: p.colors.accent }}>■</span>
              </div>
              <span className="zirro-preset-name">{p.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="zirro-field-group">
        <h5>Brand & Theme Palette</h5>

        <label className="zirro-color-field">
          <span>Primary Brand Color</span>
          <input
            type="color"
            value={colors.primary ?? "#1e3a8a"}
            onChange={(e) => handleColorChange("primary", e.target.value)}
          />
          <code>{colors.primary ?? "#1e3a8a"}</code>
        </label>

        <label className="zirro-color-field">
          <span>Secondary Brand Color</span>
          <input
            type="color"
            value={colors.secondary ?? "#0284c7"}
            onChange={(e) => handleColorChange("secondary", e.target.value)}
          />
          <code>{colors.secondary ?? "#0284c7"}</code>
        </label>

        <label className="zirro-color-field">
          <span>Highlight Accent</span>
          <input
            type="color"
            value={colors.accent ?? "#f59e0b"}
            onChange={(e) => handleColorChange("accent", e.target.value)}
          />
          <code>{colors.accent ?? "#f59e0b"}</code>
        </label>

        <label className="zirro-color-field">
          <span>Page Background</span>
          <input
            type="color"
            value={colors.background ?? "#ffffff"}
            onChange={(e) => handleColorChange("background", e.target.value)}
          />
          <code>{colors.background ?? "#ffffff"}</code>
        </label>

        <label className="zirro-color-field">
          <span>Card Surface</span>
          <input
            type="color"
            value={colors.surface ?? "#f8fafc"}
            onChange={(e) => handleColorChange("surface", e.target.value)}
          />
          <code>{colors.surface ?? "#f8fafc"}</code>
        </label>

        <label className="zirro-color-field">
          <span>Body Text</span>
          <input
            type="color"
            value={colors.text ?? "#0f172a"}
            onChange={(e) => handleColorChange("text", e.target.value)}
          />
          <code>{colors.text ?? "#0f172a"}</code>
        </label>
      </div>

      <div className="zirro-field-group">
        <h5>Typography Pairings</h5>
        <label className="zirro-field">
          <span>Heading Font</span>
          <select
            value={theme.typography?.headingFont ?? "Inter"}
            onChange={(e) =>
              updateTheme({
                typography: {
                  headingFont: e.target.value,
                  bodyFont: theme.typography?.bodyFont ?? "Inter",
                },
              })
            }
          >
            <option value="Inter">Inter (Clean Modern)</option>
            <option value="Outfit">Outfit (Bold Display)</option>
            <option value="Roboto">Roboto (Classic Sans)</option>
            <option value="Playfair Display">Playfair Display (Serif)</option>
            <option value="Space Grotesk">Space Grotesk (Tech)</option>
          </select>
        </label>
      </div>
    </div>
  );
}
